import { ClickAwayListener, Fade, MenuList, Paper, Popper, useTheme } from '@mui/material';
import React, { ForwardedRef } from 'react';
import FontAwesome from 'react-fontawesome';

import { isFunction } from '../../lib/typeCheckers';

import TriggerButton, { Trigger, TriggerButtonProps } from './TriggerButton';

export interface MenuChildProps {
  handleClose: () => void;
  ref: ForwardedRef<unknown>;
}

export type RenderProp = (props: MenuChildProps) => JSX.Element;

export interface DropdownMenuProps {
  children: React.ReactNode | RenderProp;
  className?: string;
  disablePadding?: boolean;
  trigger?: Trigger<TriggerButtonProps>;
  title: string;
}

let count = 0;
const getNewAriaId = () => `dropdown-${(count += 1)}`;

const childComponent = (children: React.ReactNode | RenderProp, handleClose: () => void) => {
  if (isFunction(children)) {
    // eslint-disable-next-line react/display-name
    const Comp = React.forwardRef((_, ref) =>
      children({
        handleClose,
        ref,
      }),
    );

    return <Comp />;
  }

  return children;
};

const defaultTrigger: Trigger<TriggerButtonProps> = (props) => (
  <TriggerButton {...props}>
    <FontAwesome name="ellipsis-v" />
  </TriggerButton>
);

const DropdownMenu = ({ children, className, title, trigger = defaultTrigger }: DropdownMenuProps): JSX.Element => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [id, _] = React.useState(getNewAriaId());

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    setOpen(!open);
  };

  const handleClose = (event?: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event?.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <React.Fragment>
      {trigger({
        anchorRef,
        id,
        onClick: handleClick,
        open,
        title,
      })}
      <Popper
        transition
        anchorEl={anchorRef.current}
        open={open}
        placement="bottom-end"
        role={undefined}
        sx={{
          '& a': {
            display: 'flex',
            padding: '10px 16px',
          },
          maxHeight: '90vh',
          zIndex: theme.zIndex.modal,
        }}
        onClick={handleClick}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocus className={className} id={id} onKeyDown={handleListKeyDown}>
                  {childComponent(children, handleClose)}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </React.Fragment>
  );
};

export default DropdownMenu;
